import { Middleware } from "redux";
import {
  AppState,
  ReportFeedbackAction,
  REPORT_APP_FEEDBACK
} from "../redux/AppTypes";
import service from "./EMService";
import {
  EMAction as BaseAction,
  FETCH_EMS as FETCH,
  LoadEMsAction as LoadAction,
  LOAD_EMS as LOAD,
  PERSIST_EM as PERSIST,
  PersistEMResult,
  REMOVE_EM as REMOVE,
  REMOVE_EM_LOCAL,
  SET_EM_CONFLUENCE_ACCESS,
  SET_EM_JIRA_ACCESS,
  UPSERT_EM
} from "./EMsTypes";

const fetchMiddleware: Middleware<{}, AppState> =
  (store) => (next) => async (action: BaseAction) => {
    const result = next(action);
    if (action.type === FETCH) {
      const page = action.paginated
        ? await service.fetchPage({
            limit: action.limit,
            nextToken: action.nextToken,
          })
        : undefined;
      const items = page ? page.items : await service.fetch();
      items.sort((i1, i2) => {
        const i1Name = `${i1.firstName} ${i1.lastName}`;
        const i2Name = `${i2.firstName} ${i2.lastName}`;
        return i1Name.toLowerCase().localeCompare(i2Name.toLowerCase());
      });
      const load: LoadAction = {
        type: LOAD,
        append: action.append,
        items,
        loadedAll: page ? !page.nextToken : true,
        nextToken: page?.nextToken,
      };
      store.dispatch(load);
      return items;
    } else {
      return result;
    }
  };

const deleteMiddleware: Middleware<{}, AppState> =
  (store) => (next) => async (action: BaseAction) => {
    const result = next(action);
    if (action.type === REMOVE) {
      await service.delete(action.id);
      store.dispatch({ type: REMOVE_EM_LOCAL, id: action.id });
      return action.id;
    } else {
      return result;
    }
  };

const persistMiddleware: Middleware<{}, AppState> =
  (store) => (next) => async (action: BaseAction) => {
    const result = next(action);
    if (action.type === PERSIST) {
      const saveResult = await service.persist(action.item);
      const item = normalizePersistResult(saveResult);
      store.dispatch({ type: UPSERT_EM, item });
      return saveResult;
    } else {
      return result;
    }
  };

function normalizePersistResult(result: PersistEMResult | any): any {
  return result?.item || result;
}

const setEMJiraAccessMiddleware: Middleware<{}, AppState> =
  (store) => (next) => async (action: BaseAction) => {
    if (action.type === SET_EM_JIRA_ACCESS) {
      const id = action.id;
      await service
        .setJiraAccess(id)
        .then(() => {
          const action: ReportFeedbackAction = {
            type: REPORT_APP_FEEDBACK,
            feedback: "Jira access granted",
          };
          store.dispatch(action);
        })
        .catch(() => {}) // errors are reported by the fetch service
        .finally(() => {
          if (action.callback) {
            action.callback();
          }
        });
    }
    return next(action);
  };

const setEMConfluenceAccessMiddleware: Middleware<{}, AppState> =
  (store) => (next) => async (action: BaseAction) => {
    if (action.type === SET_EM_CONFLUENCE_ACCESS) {
      const id = action.id;
      await service
        .setConfluenceAccess(id)
        .then(() => {
          const action: ReportFeedbackAction = {
            type: REPORT_APP_FEEDBACK,
            feedback: "Confluence access granted",
          };
          store.dispatch(action);
        })
        .catch(() => {}) // errors are reported by the fetch service
        .finally(() => {
          if (action.callback) {
            action.callback();
          }
        });
    }
    return next(action);
  };

const middlewares = [
  fetchMiddleware,
  deleteMiddleware,
  persistMiddleware,
  setEMJiraAccessMiddleware,
  setEMConfluenceAccessMiddleware,
];
export default middlewares;
